import { Injectable, computed, inject, signal } from '@angular/core';
import { PermissionService } from './permission.service';

export type Workspace = 'RMS' | 'WAREHOUSE';

export interface WorkspaceOption {
  code: Workspace;
  title: string;
  description: string;
  route: string;
}

@Injectable({ providedIn: 'root' })
export class WorkspaceService {
  private readonly permissions = inject(PermissionService);
  // Existing RMS users remain authorized; new warehouse permission is opt-in.
  private readonly options: WorkspaceOption[] = [
    {
      code: 'RMS',
      title: 'Resource Management',
      description: 'Properties, surveys, tickets and invoices',
      route: '/property',
    },
    {
      code: 'WAREHOUSE',
      title: 'Warehouse Management',
      description: 'Warehouses, material types and inventory',
      route: '/warehouse',
    },
  ];

  readonly current = signal<Workspace>('RMS');
  readonly available = computed(() => this.options.filter((option) => this.canAccess(option.code)));

  canAccess(workspace: Workspace): boolean {
    // If RMS access is separately restricted in your backend, replace true with the matching permission.
    if (workspace === 'RMS') return true;
    return this.permissions.hasPermission('warehouse:access');
  }

  setCurrent(workspace: Workspace): void {
    if (this.canAccess(workspace)) this.current.set(workspace);
  }

  landingRoute(): string {
    const allowed = this.available();
    return allowed.length > 1 ? '/select-workspace' : (allowed[0]?.route ?? '/access-denied');
  }
}
